import { useEffect, useRef } from 'react';

interface UseKeyboardDismissOptions {
  /** Container element ref to listen for scroll events. If not provided, uses window. */
  containerRef?: React.RefObject<HTMLElement | null>;
  /** Whether the hook is enabled (default: true) */
  enabled?: boolean;
}

export function useKeyboardDismiss({ containerRef, enabled = true }: UseKeyboardDismissOptions = {}) {
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const target = containerRef?.current || window;

    const handleScroll = () => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' ||
         activeElement.tagName === 'TEXTAREA' ||
         activeElement.tagName === 'SELECT') &&
        typeof (activeElement as HTMLElement).blur === 'function'
      ) {
        const currentScrollY = containerRef?.current
          ? containerRef.current.scrollTop
          : window.scrollY;

        // Only dismiss if scrolled more than 10px
        if (Math.abs(currentScrollY - lastScrollY.current) > 10) {
          (activeElement as HTMLElement).blur();
        }
        lastScrollY.current = currentScrollY;
      }
    };

    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleScroll);
  }, [containerRef, enabled]);
}

/**
 * Simple utility to dismiss the keyboard programmatically
 */
export function dismissKeyboard() {
  if (document.activeElement && typeof (document.activeElement as HTMLElement).blur === 'function') {
    (document.activeElement as HTMLElement).blur();
  }
}
